import { useEffect } from "react";
import { useSearchParams } from "react-router";
import {
  cancelOngoingConversationLoad,
  initializeChatListener,
  rehydrateConversation,
  resetChatForNewProject,
  resetChatModuleState,
  setChatHistoryLoading,
  startNewProject,
} from "../ChatSlice_Store";
import { acquireProjectSwitchLock } from "../projectSwitchLock";
import { fetchProject, setProjectId } from "../ProjectSlice_Store";
import { closeAllDocumentPreviews } from "../DocumentPreviewSlice_Store";
import { clearResults } from "../ResultsSlice_Store";
import { setLayoutMode } from "../LayoutSlice_Store";
import { useAppDispatch } from "./reduxHooks";
import type { AppDispatch } from "../types/redux";

type SetSearchParams = ReturnType<typeof useSearchParams>[1];

/** Wipe everything tied to the outgoing project before the new one loads. */
function clearWorkspace(dispatch: AppDispatch): void {
  dispatch(clearResults());
  dispatch(closeAllDocumentPreviews());
  dispatch(resetChatForNewProject());
  dispatch(setLayoutMode("chat"));
}

/** Drop the spinner and show an empty chat view. */
function showFreshChat(dispatch: AppDispatch): void {
  dispatch(setChatHistoryLoading(false));
  dispatch(setLayoutMode("chat"));
}

function putProjectIdInUrl(
  projectId: string,
  searchParams: URLSearchParams,
  setSearchParams: SetSearchParams
): void {
  const params = new URLSearchParams(searchParams);
  params.set("projectId", projectId);
  setSearchParams(params, { replace: true });
}

interface RehydrateOptions {
  dispatch: AppDispatch;
  projectId: string;
  signal: AbortSignal;
  logs: { fetched: string; complete: string; failed: string };
  onError: () => void;
}

/** Fetch project details, then rehydrate its conversation. */
function fetchThenRehydrate({
  dispatch,
  projectId,
  signal,
  logs,
  onError,
}: RehydrateOptions): void {
  dispatch(fetchProject(projectId))
    .unwrap()
    .then(() => {
      if (signal.aborted) return;
      console.log(logs.fetched);
      return dispatch(rehydrateConversation(projectId));
    })
    .then(() => {
      if (signal.aborted) return;
      console.log(logs.complete);
    })
    .catch((error) => {
      if (signal.aborted) return;
      console.error(logs.failed, error);
      // On any error (including 404), clear loading and show fresh chat view
      onError();
    });
}

/**
 * Steps 1-3 of a switch: spinner up, workspace cleared, chat module reset.
 * Returns false when a newer switch superseded this one.
 */
async function prepareForSwitch(dispatch: AppDispatch, signal: AbortSignal): Promise<boolean> {
  // Step 1: Show loading spinner immediately for instant feedback
  console.log('🔄 [PROJECT CLICK] Step 1: Showing loading spinner immediately');
  dispatch(setChatHistoryLoading(true));

  // Step 2: Clear workspace and reset chat for new project
  console.log('🔄 [PROJECT CLICK] Step 2: Clearing workspace and resetting chat');
  clearWorkspace(dispatch);

  // Step 3: Reset DirectLine connection and chat module state
  console.log('🔄 [PROJECT CLICK] Step 3: Resetting chat module state');
  await resetChatModuleState();
  if (signal.aborted) {
    console.log('🔒 [PROJECT CLICK] Superseded after resetChatModuleState — aborting');
    return false;
  }
  return true;
}

interface LoadProjectOptions {
  dispatch: AppDispatch;
  projectId: string;
  signal: AbortSignal;
  searchParams: URLSearchParams;
  setSearchParams: SetSearchParams;
}

/** Steps 4-6 of a switch: adopt the project, reconnect, rehydrate it. */
async function loadProject({
  dispatch,
  projectId,
  signal,
  searchParams,
  setSearchParams,
}: LoadProjectOptions): Promise<void> {
  // Step 4: Set project ID immediately, and reflect it in the URL
  console.log('🔄 [PROJECT CLICK] Step 4: Setting project ID');
  dispatch(setProjectId(projectId));
  putProjectIdInUrl(projectId, searchParams, setSearchParams);

  // Step 5: Reinitialize chat listener
  console.log('🔄 [PROJECT CLICK] Step 5: Reinitializing chat listener');
  await dispatch(initializeChatListener(signal));
  if (signal.aborted) {
    console.log('🔒 [PROJECT CLICK] Superseded after initializeChatListener — aborting');
    return;
  }

  // Step 6: Fetch project details, then rehydrate conversation
  console.log('🔄 [PROJECT CLICK] Step 6: Fetching project and rehydrating conversation');
  fetchThenRehydrate({
    dispatch,
    projectId,
    signal,
    logs: {
      fetched: '✅ [PROJECT CLICK] Project fetched - starting rehydration',
      complete: '✅ [PROJECT CLICK] Project click flow complete',
      failed: '❌ [PROJECT CLICK] Error in project click flow:',
    },
    onError: () => showFreshChat(dispatch),
  });
}

/** Handler for clicking a project in the sidebar. */
export function useProjectSwitching(
  currentProjectId: string | null
): (projectId: string) => Promise<void> {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  return async (projectId: string): Promise<void> => {
    console.log('🔄 [PROJECT CLICK] handleProjectClick called for projectId:', projectId);

    // Don't re-fetch if clicking the current project
    if (projectId === currentProjectId) {
      console.log('⏭️ [PROJECT CLICK] Already on this project - skipping');
      return;
    }

    // Acquire project switch lock — aborts any in-flight project switch
    const signal = acquireProjectSwitchLock();

    // Step 0: Cancel any ongoing conversation load immediately
    console.log('🔄 [PROJECT CLICK] Step 0: Cancelling any ongoing conversation load');
    cancelOngoingConversationLoad();

    // For temporary projects, just start a new project flow - no loading needed
    if (projectId.startsWith('temp-')) {
      console.log('🔄 [PROJECT CLICK] Temporary project - starting fresh chat');
      dispatch(setChatHistoryLoading(false));
      void dispatch(startNewProject());  // startNewProject acquires its own lock
      return;
    }

    if (!(await prepareForSwitch(dispatch, signal))) {
      return;
    }

    await loadProject({ dispatch, projectId, signal, searchParams, setSearchParams });
  };
}

/** Hydrate the project named in the URL, or open a listener for a brand new chat. */
async function hydrateFromUrl(
  dispatch: AppDispatch,
  urlProjectId: string | null,
  currentProjectId: string | null
): Promise<void> {
  // Acquire project switch lock — ensures only one init flow runs at a time
  const signal = acquireProjectSwitchLock();

  if (!urlProjectId || urlProjectId === currentProjectId) {
    // No URL project ID - just initialize chat listener normally (will create new project)
    console.log('🎧 [INIT] Dispatching initializeChatListener (no URL project ID)');
    void dispatch(initializeChatListener(signal));
    return;
  }

  console.log('🔄 [URL HYDRATE] Hydrating project from URL:', urlProjectId);

  // Skip hydration for temporary projects - they have no persisted data.
  // Start a new project flow instead of trying to load non-existent data.
  if (urlProjectId.startsWith('temp-')) {
    console.log('🔄 [URL HYDRATE] Temporary project detected - starting fresh chat');
    showFreshChat(dispatch);
    void dispatch(startNewProject());  // startNewProject acquires its own lock
    return;
  }

  // Show loading spinner immediately for instant feedback
  dispatch(setChatHistoryLoading(true));

  // CRITICAL: Set project ID BEFORE initializing chat listener
  // This ensures bootstrap includes the correct project_id
  console.log('🔄 [URL HYDRATE] Setting project ID before chat listener init:', urlProjectId);
  dispatch(setProjectId(urlProjectId));

  // Initialize chat listener AFTER project ID is set
  console.log('🎧 [INIT] Dispatching initializeChatListener (with project ID already set)');
  await dispatch(initializeChatListener(signal));
  if (signal.aborted) {
    console.log('🔒 [URL HYDRATE] Superseded after initializeChatListener — aborting');
    return;
  }

  fetchThenRehydrate({
    dispatch,
    projectId: urlProjectId,
    signal,
    logs: {
      fetched: '✅ [URL HYDRATE] Project fetched - starting rehydration',
      complete: '✅ [URL HYDRATE] URL hydration complete',
      failed: '❌ [URL HYDRATE] Error in URL hydration:',
    },
    onError: () => {
      console.log('🔄 [URL HYDRATE] Falling back to fresh chat view');
      showFreshChat(dispatch);
      void dispatch(startNewProject());
    },
  });
}

/**
 * Unified initialization: handles both URL hydration AND chat listener setup.
 * This fixes the race condition where both effects ran independently.
 * Skipped on admin routes — admin pages don't need project hydration.
 */
export function useChatInitialization(isAdminRoute: boolean, currentProjectId: string | null): void {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (isAdminRoute) {
      return;
    }
    void hydrateFromUrl(dispatch, searchParams.get('projectId'), currentProjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminRoute]); // Run only on mount - isAdminRoute for guard
}

/**
 * Keep the URL in step with the active project.
 * Skipped on admin routes — admin pages don't update project URL params.
 */
export function useProjectUrlSync(isAdminRoute: boolean, currentProjectId: string | null): void {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (isAdminRoute || !currentProjectId) {
      return;
    }
    // Check if URL already has this project ID to avoid unnecessary updates
    if (searchParams.get('projectId') !== currentProjectId) {
      putProjectIdInUrl(currentProjectId, searchParams, setSearchParams);
    }
  }, [currentProjectId, searchParams, setSearchParams, isAdminRoute]);
}
